/**
 * SnapshotPerformanceAnalyzer - Performance metrics and analysis for snapshot history
 */

import { HistorySnapshot } from './snapshotHistory';
import { snapshotCompressor } from './snapshotCompression';

export interface PerformanceMetrics {
  totalSnapshots: number;
  averageSnapshotSize: number;
  averageProcessingTime: number;
  memoryUsage: {
    current: number;
    peak: number;
    average: number;
  };
  compressionStats: {
    enabled: boolean;
    averageRatio: number;
    totalSaved: number;
  };
}

export interface PerformanceReport {
  metrics: PerformanceMetrics;
  recommendations: string[];
  healthScore: number; // 0-100
}

export class SnapshotPerformanceAnalyzer {
  /**
   * Get performance metrics for snapshots
   */
  static getPerformanceMetrics(
    snapshots: HistorySnapshot[],
    enableCompression: boolean
  ): PerformanceMetrics {
    const snapshotsWithMetrics = snapshots.filter(s => s.metrics);

    if (snapshotsWithMetrics.length === 0) {
      return {
        totalSnapshots: snapshots.length,
        averageSnapshotSize: 0,
        averageProcessingTime: 0,
        memoryUsage: { current: 0, peak: 0, average: 0 },
        compressionStats: { enabled: enableCompression, averageRatio: 0, totalSaved: 0 }
      };
    }

    const totalSize = snapshotsWithMetrics.reduce((sum, s) => sum + (s.metrics?.size.totalBytes || 0), 0);
    const totalTime = snapshotsWithMetrics.reduce((sum, s) => sum + (s.metrics?.performance.totalTimeMs || 0), 0);
    const memoryUsages = snapshotsWithMetrics.map(s => s.metrics?.memoryUsage.percentage || 0);

    const compressionStats = enableCompression ? snapshotCompressor.getCompressionStats() : {
      totalSnapshots: 0,
      fullSnapshots: 0,
      deltaSnapshots: 0,
      averageCompressionRatio: 0,
      totalSaved: 0
    };

    return {
      totalSnapshots: snapshots.length,
      averageSnapshotSize: totalSize / snapshotsWithMetrics.length,
      averageProcessingTime: totalTime / snapshotsWithMetrics.length,
      memoryUsage: {
        current: memoryUsages[memoryUsages.length - 1] || 0,
        peak: Math.max(...memoryUsages, 0),
        average: memoryUsages.reduce((sum, usage) => sum + usage, 0) / memoryUsages.length || 0
      },
      compressionStats: {
        enabled: enableCompression,
        averageRatio: compressionStats.averageCompressionRatio,
        totalSaved: compressionStats.totalSaved
      }
    };
  }

  /**
   * Get performance report with recommendations
   */
  static getPerformanceReport(
    snapshots: HistorySnapshot[],
    enableCompression: boolean
  ): PerformanceReport {
    const metrics = this.getPerformanceMetrics(snapshots, enableCompression);
    const recommendations: string[] = [];
    let healthScore = 100;

    // Analyze performance metrics and generate recommendations
    if (metrics.averageSnapshotSize > 1024 * 1024) { // > 1MB
      recommendations.push('🔴 Large snapshot size detected. Consider enabling compression or reducing data size.');
      healthScore -= 20;
    } else if (metrics.averageSnapshotSize > 512 * 1024) { // > 512KB
      recommendations.push('🟡 Moderate snapshot size. Monitor for growth trends.');
      healthScore -= 10;
    }

    if (metrics.averageProcessingTime > 50) { // > 50ms
      recommendations.push('🔴 Slow snapshot processing. Consider enabling background processing.');
      healthScore -= 15;
    } else if (metrics.averageProcessingTime > 20) { // > 20ms
      recommendations.push('🟡 Moderate processing time. Consider optimization.');
      healthScore -= 5;
    }

    if (metrics.memoryUsage.peak > 80) {
      recommendations.push('🔴 High memory usage detected. Consider reducing snapshot retention.');
      healthScore -= 25;
    } else if (metrics.memoryUsage.peak > 60) {
      recommendations.push('🟡 Moderate memory usage. Monitor for trends.');
      healthScore -= 10;
    }

    if (enableCompression && metrics.compressionStats.averageRatio < 0.3) {
      recommendations.push('🟡 Low compression efficiency. Data may not be suitable for delta compression.');
      healthScore -= 5;
    }

    if (recommendations.length === 0) {
      recommendations.push('🟢 All performance metrics are within optimal ranges.');
    }

    return {
      metrics,
      recommendations,
      healthScore: Math.max(0, healthScore)
    };
  }
}
